import { Alert, App as AntApp, DatePicker, Form, Modal } from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs, { type Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, applyFieldErrors } from "../app/apiError.ts";
import { billingKeys, formatDate, todayIso, type RuleVersion } from "../app/billing.ts";

/** Field name matches the API so the server's validTo error lands under the date picker. */
interface EndForm {
  validTo?: Dayjs;
}

interface Props {
  /** The open version to end; undefined keeps the dialog closed. */
  version: RuleVersion | undefined;
  label: string;
  end(id: string, validTo: string): Promise<{ error?: unknown; response: Response }>;
  onClose(): void;
}

/**
 * Ends an open version on a chosen last day without adding a successor (bd-2sz.14), e.g. to regroup teacher-rate grade
 * bands or stop charging a discontinued service. The picker offers no day before the start or the last day an invoice used.
 */
export function EndVersionModal({ version, label, end, onClose }: Props) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<EndForm>();
  const [summary, setSummary] = useState<string[]>([]);
  // ISO dates compare as strings; the last day may equal the start or the last day an invoice used.
  const earliest = version && (version.usedThrough && version.usedThrough > version.validFrom ? version.usedThrough : version.validFrom);
  // A future version would otherwise open the calendar on the current month with every day disabled.
  const openOn = earliest !== undefined && earliest > todayIso() ? dayjs(earliest) : undefined;

  useEffect(() => {
    if (version === undefined) return;
    form.resetFields();
    setSummary([]);
  }, [version, form]);

  const save = useMutation({
    mutationFn: async (values: EndForm) => {
      if (!version || !values.validTo) return;
      const { error, response } = await end(version.id, values.validTo.format("YYYY-MM-DD"));
      if (!response.ok) throw new ApiError(response.status, error);
    },
    onSuccess: async () => {
      await Promise.all([queryClient.invalidateQueries({ queryKey: billingKeys.all }), queryClient.invalidateQueries({ queryKey: ["audit"] })]);
      void message.success(t("billing.end.done"));
      onClose();
    },
    onError: (err) => setSummary(applyFieldErrors(form, err, t("app.error"))),
  });

  return (
    <Modal
      open={version !== undefined}
      title={t("billing.end.title", { name: label })}
      okText={t("billing.end.confirm")}
      okButtonProps={{ danger: true }}
      cancelText={t("common.cancel")}
      confirmLoading={save.isPending}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnHidden
    >
      {summary.length > 0 && <Alert type="error" showIcon role="alert" style={{ marginBottom: 16 }} title={summary.map((m) => <div key={m}>{m}</div>)} />}
      <Alert type="warning" showIcon style={{ marginBottom: 16 }} title={t("billing.end.hint")} />
      <Form<EndForm> form={form} layout="vertical" requiredMark={false} onFinish={(values) => save.mutate(values)}>
        <Form.Item
          name="validTo"
          label={t("billing.end.validTo")}
          rules={[{ required: true, message: t("common.required") }]}
          extra={earliest ? t("billing.end.earliest", { date: formatDate(earliest) }) : undefined}
        >
          <DatePicker
            style={{ width: "100%" }}
            format="DD.MM.YYYY"
            {...(openOn ? { defaultPickerValue: openOn } : {})}
            disabledDate={(day) => earliest !== undefined && day.format("YYYY-MM-DD") < earliest}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
